import axios from 'axios';
import { getActivePinia } from 'pinia';
import { readFetchFailure } from 'pb-api-errors';
import { WRITE_TIMEOUT_MS } from '~/composables/api';
import { authCookieNames } from '~/utils/auth-cookies';
import { oauthAuthorizeUrl } from '~/utils/auth-redirect';
import { tokenIn } from '~/utils/session-token';
import { notifySupportChatIdentityChanged, resetSupportChat } from '~/composables/useChatWidget';
import type { PbAccessToken } from '~/types/auth/auth';

/** The pair a sign-in or a renewal hands the store: all it writes and holds of what the API minted. */
type SessionPair = Pick<PbAccessToken, 'access_token' | 'refresh_token'>;

/**
 * The member an access token names: its `sub`, which Passport mints as the member's person id, a
 * string of digits. Null for anything else: no token, one that is not a JWT, a payload that is not
 * JSON, or a `sub` that is not a person id written the way Passport writes one.
 */
function memberOf(accessToken: string | null): number | null {
    const parts = accessToken?.replace(/^Bearer /, '').split('.') ?? [];
    const [, payload] = parts;
    if (parts.length !== 3 || !payload) return null;

    let claims: unknown;
    try {
        claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
    const subject = typeof claims === 'object' && claims !== null && 'sub' in claims ? claims.sub : undefined;
    if (typeof subject !== 'string' || !/^[1-9]\d*$/.test(subject)) return null;

    const member = Number(subject);
    return Number.isSafeInteger(member) ? member : null;
}

/** An access token cookie's value, in the form requests carry it; null when it holds no token (tokenIn). */
function bearerIn(value: unknown): string | null {
    const token = tokenIn(value);
    if (token === null) return null;

    return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

/**
 * Run a renewal of the session once every renewal this app's other tabs asked for first has run.
 * The API spends a refresh token as it redeems it, so of two tabs presenting the same one, the
 * second would be refused. A browser's locks are per origin: the other Privacy Bee apps renew the
 * same session on their own, and a refusal they cause is settled against the jar. Outside a
 * browser there are no locks, and nothing else to wait for.
 */
async function afterEarlierRenewals(renew: () => Promise<boolean>): Promise<boolean> {
    const locks = typeof navigator === 'undefined' ? null : navigator.locks;

    return locks ? await locks.request('pb-token-refresh', { mode: 'exclusive' }, renew) : await renew();
}

export const useAuthStore = defineStore(
    'auth',
    () => {
        const config = useRuntimeConfig();
        // This store's pinia. The person store beside it is resolved where it is used rather than
        // created here: its saved identity is checked against this store's session as it hydrates
        // (stores/person.ts), which needs this store complete.
        const pinia = getActivePinia();

        // Environment-scoped cookie names (unsuffixed on prod/local, `.dev` on develop).
        // Every read/write/clear below must use these so an environment only ever touches its own
        // session — see app/utils/auth-cookies.ts.
        const { tokenCookieName, refreshCookieName } = authCookieNames(config.public.pb.authCookieSuffix);

        // The session lives in cookies on the parent domain. A cookie of the same name on this host
        // (a write through a useCookie ref leaves one, docs/GOTCHAS.md) is listed beside the
        // session's, first once the session's is the newer, and every read of a cookie by name here
        // takes the first. So the host-only form goes before anything reads the jar; deleting it
        // leaves the session's cookies alone.
        if (!import.meta.server) {
            deleteCookie(tokenCookieName);
            deleteCookie(refreshCookieName);
        }

        // The access token's cookie, as the browser announces changes to it. The session lives on
        // the parent domain, where every Privacy Bee app reads and renews it, and this store only
        // listens to it: a write through a useCookie ref carries no domain, so it would put a
        // host-only copy of an auth cookie beside the shared one, which a sign-out on the auth host
        // cannot delete and which a read of document.cookie can find in place of the shared one.
        const tokenCookie = useCookie<string | null>(tokenCookieName, { default: () => null, readonly: true });

        // Whether this document has handed its member over (handOver): the jar came to hold a
        // session that is not its own. From then on it holds no session and takes none, until a
        // sign-in here, or a page load that boots as whoever the jar holds.
        let handedOver = false;

        // The access token this document's requests carry. It starts as the jar holds it, and follows
        // every change announced to it the moment it is announced, as far as the session is this
        // document's to send (sessionIsOurs). What this document learns first, it holds at once: the
        // pair a sign-in writes (setToken), or a session it takes from the jar. It holds no refresh
        // token: a renewal presents the one in the jar, where every tab and app writes the session
        // (renewSession).
        const token = ref(bearerIn(tokenCookie.value));

        // The browser announces a deletion by the cookie's name alone, whichever cookie of that name
        // went: the host-only copy a page deletes as it opens (above), in this tab or another, as much
        // as the session's own. useCookie sets its ref to null for either, so a deletion leaves the
        // session as the jar holds it. And a ref left at null that way hears no deletion after it, as
        // setting a ref to the value it holds tells no one: so where the browser announces changes
        // (the Cookie Store API), this store hears every deletion itself as well. That includes the
        // refresh token's, which has no ref: once the access token has lapsed, it is all that is left
        // of the session, and a sign-out elsewhere deletes only it.
        watch(
            () => tokenCookie.value,
            (announced) => followAccessToken(announced === null ? jarToken() : bearerIn(announced)),
            { flush: 'sync' },
        );
        if (import.meta.client && 'cookieStore' in window) {
            const followDeletions = ({ deleted }: CookieChangeEvent) => {
                if (deleted.some(({ name }) => name === tokenCookieName || name === refreshCookieName)) followAccessToken(jarToken());
            };
            window.cookieStore.addEventListener('change', followDeletions);
            onScopeDispose(() => window.cookieStore.removeEventListener('change', followDeletions));
        }

        /**
         * Follow `inJar`, the access token the jar holds: take it as far as this document takes a
         * session (takeAccessToken), or let go of the one held when there is none.
         */
        function followAccessToken(inJar: string | null) {
            if (handedOver) return;

            if (inJar !== null) {
                takeAccessToken(inJar);
                return;
            }

            // With the refresh token still in the jar, the access token lapsed, and a renewal brings
            // the session back. With neither, the session ended elsewhere (a sign-out on the auth
            // host or in another app), and a document that held it, or shows its member's account,
            // lets go of that member.
            const hadMember = token.value !== null || usePersonStore(pinia).person !== null;
            token.value = null;
            if (hadMember && !jarRefreshToken()) releaseMember();
        }

        let refreshPromise: Promise<boolean> | null = null;

        const loggedIn = computed(() => !!token.value);

        /** The member the session's access token names; null when it holds none, or one that names no one. */
        const sessionMember = computed(() => memberOf(token.value));

        /**
         * The cookie `name` as the jar holds it: the value of the first of that name document.cookie
         * lists, all of it (a value runs to the end of its pair, `=` included). Null for none.
         */
        function readCookieDirectly(name: string): string | null {
            if (import.meta.server) return null;

            const prefix = `${name}=`;
            const cookie = document.cookie
                .split(';')
                .map((pair) => pair.trim())
                .find((pair) => pair.startsWith(prefix));
            return cookie === undefined ? null : decodeURIComponent(cookie.slice(prefix.length));
        }

        /** The access token in the jar, in the form requests carry it. */
        function jarToken(): string | null {
            return bearerIn(readCookieDirectly(tokenCookieName));
        }

        /** The refresh token in the jar. */
        function jarRefreshToken(): string | null {
            return tokenIn(readCookieDirectly(refreshCookieName));
        }

        /**
         * Whether a session of `member` is this document's to send: the session of the member whose
         * account it holds, or anyone's while it holds no one's, and never once it has handed its
         * member over (handOver). A session whose member cannot be read (null) is nobody's.
         */
        function sessionIsOurs(member: number | null): boolean {
            if (handedOver || member === null) return false;

            const ours = usePersonStore(pinia).person?.identity.id;
            return ours === undefined || ours === member;
        }

        /**
         * Take an access token this document did not write: from the browser's announcement, from
         * the jar, or from a renewal. It is held when its session is this document's own
         * (sessionIsOurs), and the member is handed over when it is not. A session the jar holds
         * with no access token names no one. Answers whether this document holds it.
         */
        function takeAccessToken(accessToken: string | null): boolean {
            if (accessToken === null || !sessionIsOurs(memberOf(accessToken))) {
                // The first hand-over sends a page that needs a session to sign in (goToSignIn),
                // with no page to return to: whoever holds the session starts on checkout's own
                // start page, and the page this document is on is its member's (a business order
                // names their company).
                if (!handedOver) {
                    handOver();
                    void goToSignIn('start page');
                }
                return false;
            }

            if (accessToken !== token.value) {
                token.value = accessToken;
                // A live chat widget holds the bearer it bootstrapped with for the life of its
                // socket, so it must be told to re-bootstrap. No-op when chat was never opened.
                notifySupportChatIdentityChanged();
            }
            return true;
        }

        /**
         * The jar holds a session that is not this document's to send (sessionIsOurs): another
         * member signed in on the auth host or in another app, or its member cannot be read. It is
         * left to whoever holds it. This document lets go of its member (releaseMember) and its
         * session, and holds and takes no session again until a sign-in here, so nothing it sends
         * reaches that account.
         */
        function handOver() {
            handedOver = true;
            releaseMember();
            token.value = null;
        }

        /**
         * This document lets go of the member whose account it shows: the identity every session it
         * is offered is checked against (sessionIsOurs), which the page shows, prefills and charges
         * for, and the support widget, which keeps the socket and transcript of the member it
         * bootstrapped as. The next person at this browser is shown neither, and the next member to
         * sign in here is this document's member.
         */
        function releaseMember() {
            resetSupportChat();
            usePersonStore(pinia).clear();
        }

        // Helper to check if current route is public
        function isPublicRoute(): boolean {
            if (import.meta.server) return false;

            try {
                const router = useRouter();
                const route = router.currentRoute.value;
                return route.meta.public === true;
            } catch {
                // Fallback: check pathname against known public routes
                const publicPaths = ['/auth/login', '/auth/callback', '/auth/logout', '/brokers'];
                return publicPaths.some(path => window.location.pathname.startsWith(path));
            }
        }

        // The token a request carries, renewing the session when there is none to send
        async function getValidTokenForRequest(): Promise<string | null> {
            // A document that handed its member over sends no session: the one in the jar is not its own.
            if (handedOver) {
                return null;
            }

            // The access token this document holds
            if (token.value) {
                return token.value;
            }

            // Fallback: the jar itself. A session written after this document read the jar (a
            // sign-in in another tab or app) reaches it a task later at the soonest, and never in a
            // browser without the Cookie Store API.
            const fromJar = jarToken();
            if (fromJar) {
                return takeAccessToken(fromJar) ? fromJar : null;
            }

            // If we're on a public route and don't have a token, return null (don't try to refresh)
            if (isPublicRoute()) {
                return null;
            }

            // No token: renew, or join the renewal already running (refreshAccessToken)
            const refreshed = await refreshAccessToken();
            return refreshed ? token.value : null;
        }

        /**
         * Renew the session. Resolves true once this document holds an access token to send; false
         * once it holds none, because the session ended or is not its own. Rejects when the API
         * could not be asked (no connection, a timeout, a 5xx), which says nothing about the
         * session: it stays, and the caller reports the failure for the buyer to retry.
         */
        async function refreshAccessToken(): Promise<boolean> {
            // A document that handed its member over renews nothing: the session in the jar is not its own.
            if (handedOver) {
                return false;
            }

            // If already refreshing, return the existing promise
            if (refreshPromise) {
                return refreshPromise;
            }

            // The access token this document was sending: a renewal another tab or app makes while
            // this one waits its turn replaces it in the jar.
            const replaced = token.value;
            refreshPromise = afterEarlierRenewals(() => renewSession(replaced)).finally(() => {
                refreshPromise = null;
            });

            return refreshPromise;
        }

        async function renewSession(replaced: string | null): Promise<boolean> {
            // Another tab or app renewed the session, and the refresh token this document would
            // present is spent: what was written answers this renewal. This app's other tabs renew
            // while this one waits its turn; another Privacy Bee app renews at any time. Either
            // reaches this document a task later at the soonest, and never in a browser without the
            // Cookie Store API.
            const renewedElsewhere = jarToken();
            if (renewedElsewhere && renewedElsewhere !== replaced) {
                return takeAccessToken(renewedElsewhere);
            }

            // The jar's refresh token, never one this document held: the jar is where every tab and
            // app writes the session, so a held one is spent by the next renewal anywhere else. A jar
            // with none holds no session. It ended, or was cleared, and a token from before would
            // bring back a session a sign-out ended.
            const presented = jarRefreshToken();
            if (!presented) {
                await endSession();
                return false;
            }

            try {
                // Sessions are minted under the API's confidential client, so only the API
                // can redeem their refresh tokens; Passport's /oauth/token rejects them from
                // this public client ("Token is not linked to client"). Bounded as a write is
                // (composables/api.ts): the API spends the token it redeems, so a renewal cut short
                // after it answered leaves the jar holding a spent one.
                const response = await axios.post<{ data: PbAccessToken }>(
                    `${config.public.pb.authHost}/v3/auth/refresh`,
                    { refresh_token: presented },
                    { timeout: WRITE_TIMEOUT_MS },
                );

                // The jar as the answer lands. A session ended while this renewal was on its way (a
                // sign-out in another tab or app emptied the jar) stays ended: writing the pair just
                // minted would sign the member back in on every app. A session written meanwhile is
                // newer than that pair, and writing the pair over it would sign it out.
                const inJarNow = jarRefreshToken();
                if (!inJarNow) {
                    await endSession();
                    return false;
                }
                if (inJarNow !== presented) {
                    return takeAccessToken(jarToken());
                }

                // The refresh token presented is spent, so the pair is the session's next, whoever's
                // it is, and it goes where every app reads the session. A refresh token says nothing
                // of whose session it renews, so the pair's access token says it, and this document
                // sends the pair only if it is its own.
                const minted = response.data.data;
                writeSession(minted);
                return takeAccessToken(`Bearer ${minted.access_token}`);
            } catch (error) {
                // Only a 401 — the one status app-api's /v3/auth/refresh answers a refused, expired,
                // or reused token with — says the session is over. A renewal it never answered (no
                // connection, a timeout, a 5xx) says nothing, and neither does an edge 400/403 (a
                // Cloudflare challenge or a misroute: the refresh route itself never answers 400 or
                // 403). The cookies are shared by every Privacy Bee app: ending the session would
                // sign the member out of all of them over a failure a second try can recover from. So
                // the session stays, and the failure goes to every request waiting on this renewal and
                // to the caller, which the buyer can retry.
                const { status } = readFetchFailure(error);
                if (status !== 401) {
                    throw error;
                }

                // A refusal says the token presented is spent, not whose session is left. The
                // session is over only while the jar still holds the token refused, or nothing. A
                // token another tab or app renewed with, or a session another member signed into,
                // lives on, and ending it would sign it out of every Privacy Bee app.
                const inJar = jarRefreshToken();
                if (!inJar || inJar === presented) {
                    await endSession();
                    return false;
                }

                return takeAccessToken(jarToken());
            }
        }

        /**
         * The session is over: the API refused the refresh token the jar still holds, or the jar
         * holds none to present. This document lets go of its member (releaseMember), the cookies
         * go (shared, so from every Privacy Bee app), and a page that needs a session sends the
         * buyer to sign in and back to where they were.
         */
        async function endSession() {
            token.value = null;
            releaseMember();

            // Clear cookies from browser (not just memory)
            if (import.meta.client) {
                clearAuthCookies();
            }

            // The member signs in again and comes back to the page they were on.
            await goToSignIn('this page');
        }

        /**
         * Send a page that needs a session to sign in, the way the route middleware sends a page with
         * no session, and after it to the page it is on or to checkout's start page. The callback
         * lands the buyer only on a path of this site (utils/safe-return-path.ts), and only for the
         * nonce this tab stored as it left.
         */
        async function goToSignIn(returnTo: 'this page' | 'start page') {
            if (isPublicRoute()) return;
            const page = useRouter().currentRoute.value;
            if (page.path.startsWith('/auth/')) return;
            await navigateTo(oauthAuthorizeUrl(config.public.pb, { path: returnTo === 'this page' ? page.fullPath : '/' }), { external: true });
        }

        /** Delete the cookie `name` this host sees: the one written for `domain`, or the host-only one. */
        function deleteCookie(name: string, domain?: string) {
            const scope = domain ? `; domain=${domain}` : '';
            document.cookie = `${name}=; path=/${scope}; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax; Secure`;
        }

        // Helper function to clear auth cookies from browser
        function clearAuthCookies() {
            // Only this environment's names — on dev these are the suffixed variants, so we
            // never expire the unsuffixed production cookies (that would log the user out of prod).
            // Both forms go: the session's on the parent domain, and a copy on this host.
            for (const name of [tokenCookieName, refreshCookieName, 'XSRF-TOKEN']) {
                deleteCookie(name, config.public.pb.domainHost);
                deleteCookie(name);
            }
        }

        /**
         * Restore the session for a page that shows the buyer's account: the access token this
         * document holds, or a renewal of the one that lapsed. 'signed-in' once this document holds
         * one; 'signed-out' when there is none to hold (no session, it ended, or it is another
         * member's); 'unanswered' when the API never answered the renewal (no connection, a
         * timeout, a 5xx). The session then stays, and the buyer is neither signed in on the page
         * nor known to be signed out, so the page says so rather than treat them as either.
         */
        async function initializeAuth(): Promise<'signed-in' | 'signed-out' | 'unanswered'> {
            if (token.value) {
                return 'signed-in';
            }

            try {
                return (await refreshAccessToken()) ? 'signed-in' : 'signed-out';
            } catch {
                return 'unanswered';
            }
        }

        /** Put a pair the API minted where every Privacy Bee app reads the session: on the parent domain. */
        function writeSession(tokenData: SessionPair) {
            const domain = config.public.pb.domainHost;
            const tokenExpires = new Date(Date.now() + (12 * 60 * 60 * 1000)); // 12 hours
            const refreshExpires = new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)); // 30 days

            document.cookie = `${tokenCookieName}=Bearer%20${tokenData.access_token}; path=/; domain=${domain}; expires=${tokenExpires.toUTCString()}; secure; samesite=lax`;
            document.cookie = `${refreshCookieName}=${tokenData.refresh_token}; path=/; domain=${domain}; expires=${refreshExpires.toUTCString()}; secure; samesite=lax`;
        }

        // Set token (from a sign-in: the OAuth callback, or an inline sign-in)
        function setToken(tokenData: SessionPair) {
            writeSession(tokenData);

            // A sign-in here is this document's own session, whoever's it is. A browser with the
            // Cookie Store API announces the writes above back to this document a task later, and
            // they are taken only as the session of the member whose account it shows
            // (sessionIsOurs). So an account it still shows that is not the member signing in goes
            // first.
            handedOver = false;
            const signedIn = `Bearer ${tokenData.access_token}`;
            const shown = usePersonStore(pinia).person;
            if (shown && shown.identity.id !== memberOf(signedIn)) releaseMember();
            // Hold it at once so `token` / `loggedIn` update synchronously: without this a
            // just-signed-in session (OAuth callback, inline sign-in) would read as logged out until
            // the writes came back, and the page would open the logged-out picker and skip the
            // authed bootstrap.
            token.value = signedIn;

            // A live chat widget holds the OLD bearer for the life of its socket,
            // so it must be told to re-bootstrap — and that reconnect is what
            // merges a buyer's anonymous conversation into their account after an
            // inline sign-in, which navigates nothing. Browser-only by its own
            // guard, so it needs no gate here.
            notifySupportChatIdentityChanged();
        }

        return {
            token: readonly(token), // Make token read-only
            loggedIn,
            sessionMember,
            setToken,
            refreshAccessToken,
            getValidTokenForRequest,
            initializeAuth,
        };
    },
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
